import api from "api"

export async function loadHousehold(store, household_id) {
  const household = await api.get(`households/${household_id}`)
  await store.update(state => {
    state.households[household.id] = household
  })
}

export async function updateHousehold(store, { id, ...values }) {
  const { is_renter, has_confirmed_properties } = values
  if (is_renter && !has_confirmed_properties) {
    Object.assign(values, { has_confirmed_properties: true })
  }
  const household = await api.patch(`households/${id}`, undefined, values)
  await store.update(state => {
    state.households[household.id] = household
    delete state.incomeBreakdowns[id]
  })
}

export async function loadUserHouseholdIds(store, group_id) {
  const households = await api.get(`households`)
  households.sort((a, b) => a.updated_at < b.updated_at)
  await store.update(state => {
    state.householdIds = {
      [group_id]: households.map(({ id }) => id)
    }
    households.forEach(
      household => (state.households[household.id] = household)
    )
  })
}

export async function loadHouseholdIncomeBreakdown(store, household_id) {
  const incomeBreakdown = await api.get(
    `households/${household_id}/snapshots/general-spending`
  )
  await store.update(state => {
    state.incomeBreakdowns[household_id] = incomeBreakdown
  })
}

export async function loadHouseholdSpendingBreakdown(store, household_id) {
  const spendingBreakdown = await api.get(
    `households/${household_id}/snapshots/spending`
  )
  await store.update(state => {
    state.spendingBreakdowns[household_id] = spendingBreakdown
  })
}

export async function loadHouseholdState(store, household_id) {
  if (!store.getState("households", household_id)) {
    await store.actions.loadHousehold(household_id)
  }

  if (!store.getState("householdScenarios", household_id)) {
    await store.actions.loadHouseholdScenarios(household_id)
  }
  const currentScenarioId = store.getState(
    "householdScenarioIds",
    household_id
  )[0]

  if (!store.getState("scenarioProposalIds", currentScenarioId)) {
    await store.actions.loadScenarioProposalStatuses(currentScenarioId)
  }
  const proposals = store.getState("scenarioProposalIds", currentScenarioId)
  const isEditingPlan =
    (window && /.*\/goals\+decisions.*/.test(window.location.pathname)) ||
    proposals.length === 0
  // if (!store.getState('householdAccountIds', household_id)) {
  //   await store.actions.loadHouseholdAccounts(household_id)
  // }
  // const hasAccounts = store.getState('householdAccountIds', household_id).length > 0

  // if (!store.getState('scenarioGoals', currentScenarioId)) {
  //   await store.actions.loadScenarioGoals(currentScenarioId)
  // }
  // const hasGoals = store.getState('scenarioGoalIds', currentScenarioId).length > 0

  await store.update(state => {
    if (!state.householdState[household_id]) {
      state.householdState[household_id] = {}
    }
    state.householdState[household_id]["currentScenarioId"] = currentScenarioId
    state.householdState[household_id]["muteActionPlanWarning"] = false
    state.householdState[household_id]["isEditingPlan"] = isEditingPlan
  })
}

export async function updateHouseholdState(store, { id, ...values }) {
  if (!store.getState("householdState", id)) {
    await store.actions.loadHouseholdState(id)
  }
  // TODO
  await store.update(state => {
    Object.keys(values).forEach(
      key => (state.householdState[id][key] = values[key])
    )
  })
}

export async function setupNewHousehold(store, { household_id, ...data }) {
  await api.patch(
    `households/${household_id}`,
    {},
    {
      primary_state_of_residence: data.primary_state_of_residence
    }
  )

  await api.post(
    `households/${household_id}/people`,
    {},
    {
      first_name: data.first_name,
      birth_date: data.birth_date,
      role: "client"
    }
  )

  if (data.marital_status === "married") {
    await api.post(
      `households/${household_id}/people`,
      {},
      {
        first_name: data.spouse.first_name,
        birth_date: data.spouse.birth_date,
        role: "spouse"
      }
    )
  }

  for (let child of data.children_details) {
    await api.post(
      `households/${household_id}/people`,
      {},
      {
        first_name: child.first_name,
        birth_date: child.birth_date,
        role: "child"
      }
    )
  }

  await store.actions.loadHousehold(household_id)
  await store.actions.loadUserHouseholdIds(store.getState("user").group_id)
  await store.actions.loadHouseholdPeople(household_id)
  await store.actions.setCurrentHouseholdId(household_id)
}

export async function addPerson(store, { household_id, ...data }) {
  const person = await api.post(
    `households/${household_id}/people`,
    {},
    {
      first_name: data.first_name,
      birth_date: data.birth_date,
      role: data.role
    }
  )

  await store.update(state => {
    if (!state.householdAccountIds[household_id]) {
      state.householdPersonIds[household_id] = []
    }
    state.householdPersonIds[household_id].push(person.id)
    state.people[person.id] = person
    delete state.incomeBreakdowns[household_id]
  })
  return {
    personId: person.id
  }
}

export async function createAndSetupNewHousehold(store, values) {
  const { id } = await api.post("households", { advisee: null }, {})

  await store.actions.setupNewHousehold({ household_id: id, ...values })
}

export async function loadPoll(store, household_id) {
  const user = store.getState("user")
  if (user.group_type === "advisor") {
    await store.update(state => {
      state.polls = {
        [household_id]: {
          confidence: "",
          responded_at: ""
        }
      }
    })
    return
  }
  try {
    const response = await api.get(
      `households/${household_id}/polls/confidence/details`
    )
    await store.update(state => {
      if (state.polls == null) {
        state.polls = {
          [household_id]: {
            confidence: response.choice,
            responded_at: response.responded_at
          }
        }
      } else if (state.polls[household_id] == null) {
        state.polls[household_id] = {
          confidence: response.choice,
          responded_at: response.responded_at
        }
      } else {
        state.polls[household_id]["confidence"] = response.choice
        state.polls[household_id]["responded_at"] = response.responded_at
      }
    })
  } catch (error) {
    await store.update(state => {
      state.polls = {
        [household_id]: {
          confidence: "",
          responded_at: ""
        }
      }
    })
  }
}

export async function addPoll(store, values) {
  const { household_id, option } = values
  const response = await api.post(
    `households/${household_id}/polls/confidence/details`,
    {},
    {
      choice: option
    }
  )
  await store.update(state => {
    state.householdState[household_id]["showOrganizeIntroduction"] = false
    state.polls[household_id]["confidence"] = option
    state.polls[household_id]["responded_at"] = response.responded_at
  })
}

export async function deleteHousehold(store, household_id) {
  try {
    await api.delete(`households/${household_id}`)
    await api.get(`households`) // re-create user's household if needed
  } catch (error) {
    console.error(error)
  }

  await store.update(state => {
    delete state.households[household_id]
    delete state.householdState[household_id]

    state.householdPersonIds[household_id] &&
      state.householdPersonIds[household_id].forEach(personId => {
        delete state.people[personId]
      })
    delete state.householdPersonIds[household_id]

    state.householdAccountIds[household_id] &&
      state.householdAccountIds[household_id].forEach(accountId => {
        delete state.accounts[accountId]
      })
    delete state.householdAccountIds[household_id]

    state.householdConnectionsIds[household_id] &&
      state.householdConnectionsIds[household_id].forEach(connectionId => {
        delete state.connections[connectionId]
      })
    delete state.householdConnectionsIds[household_id]

    state.householdConnectionLinksIds[household_id] &&
      state.householdConnectionLinksIds[household_id].forEach(
        connectionLinkId => {
          delete state.householdConnectionLinksIds[connectionLinkId]
        }
      )
    delete state.householdConnectionLinksIds[household_id]

    state.householdPropertyIds[household_id] &&
      state.householdPropertyIds[household_id].forEach(propertyId => {
        delete state.properties[propertyId]
      })
    delete state.householdPropertyIds[household_id]

    state.householdIncomeIds[household_id] &&
      state.householdIncomeIds[household_id].forEach(incomeId => {
        delete state.incomes[incomeId]
      })
    delete state.householdIncomeIds[household_id]

    delete state.householdEmployerIds[household_id]

    state.householdBenefitIds[household_id] &&
      state.householdBenefitIds[household_id].forEach(benefitId => {
        delete state.benefits[benefitId]
      })
    delete state.householdBenefitIds[household_id]

    delete state.incomeBreakdowns[household_id]
    delete state.spendingBreakdowns[household_id]

    state.householdScenarioIds[household_id] &&
      state.householdScenarioIds[household_id].forEach(scenarioId => {
        state.scenarioGoalIds[scenarioId] &&
          state.scenarioGoalIds[scenarioId].forEach(goalId => {
            delete state.goals[goalId]
          })
        delete state.scenarioGoalIds[scenarioId]
      })
    delete state.householdScenarioIds[household_id]
  })
}

export async function purgeUserHouseholdData(store) {
  const household_id = store.getState("user", "group_id")
  await store.actions.deleteHousehold(household_id)
}

export async function loadInterview(store, interview_id) {
  const [household_id, interview_type] = interview_id.split("/", 2)
  const interview_data = await api.get(
    `households/${household_id}/interviews/${interview_type}`
  )
  await store.update(state => {
    state.interviews[interview_id] = interview_data
  })
}
